import React, { Fragment, useState } from "react";
import "./TaskList.scss";
import Input from "../input/input";
import Task from "./Task";
import ClickableText from "../clickableText/ClickableText";
import { Timestamp } from "firebase/firestore";
import WidgetHeaderContent from "../widgetHeaderContent/WidgetHeaderContent";
import H, { headerSizes } from "../elements/H";

import { addTask, filterTasks } from "../../actions/taskActions";
import { useAppSelector, useAppDispatch } from "../../hooks";
import type { ButtonActivationEvent } from "../button/button";
import type { Task as TaskModel } from "../../types/models";

export interface TaskListProps {
  widget_id?: string;
}

export default function TaskList(props: TaskListProps) {
  const [currentTaskName, setCurrentTaskName] = useState("");
  const dispatch = useAppDispatch();
  const { uid } = useAppSelector(state => state.userDetails);
  const { isCompletedFilter, taskList } = useAppSelector(state => state.task);

  const addNewTask = (event: React.FormEvent) => {
    event.preventDefault();
    if (currentTaskName) {
      const task = {
        id: null,
        taskName: currentTaskName,
        createdDate: Timestamp.now(),
        uid,
        isCompleted: false,
        isArchived: false
      };

      dispatch(addTask(task));
      setCurrentTaskName("");
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTaskName(event.target.value);
  };

  const generateTaskList = (
    sortedTaskList: TaskModel[],
    isCompletedList: boolean
  ) => {
    return (
      <div className="task-list">
        {sortedTaskList.map((task: TaskModel) => {
          return (
            <Task key={task.id} task={task} isCompletedList={isCompletedList} />
          );
        })}
      </div>
    );
  };

  const getHeader = (isCompletedFilter: boolean) => {
    if (isCompletedFilter) {
      return null;
    } else {
      return (
        <form onSubmit={addNewTask}>
          <div className="add-task-container">
            <Input
              placeholder="Task Name"
              id={`taskName_${props.widget_id}`}
              type="text"
              name="currentTaskName"
              value={currentTaskName}
              onChange={handleChange}
              className="body-layout-font"
            />
          </div>
        </form>
      );
    }
  };

  const getPage = (
    sortedTaskList: TaskModel[],
    isCompletedFilter: boolean
  ) => {
    if (isCompletedFilter) {
      if (sortedTaskList.length > 0) {
        return (
          <div className="task-list-container">
            {generateTaskList(sortedTaskList, true)}
          </div>
        );
      } else {
        return (
          <div className="task-list-container">
            <p className="body-layout-font">
              <em>You haven't closed any tasks yet!</em>
            </p>
          </div>
        );
      }
    } else {
      return (
        <div className="task-list-container">
          {generateTaskList(sortedTaskList, false)}
        </div>
      );
    }
  };

  const onCompletionFilterClick = (event: ButtonActivationEvent) => {
    event.preventDefault();
    dispatch(filterTasks(!isCompletedFilter));
  };

  const sortFn = (a: TaskModel, b: TaskModel) => {
    if (a.createdDate && b.createdDate) {
      return (
        new Timestamp(
          b.createdDate.seconds,
          b.createdDate.nanoseconds
        )
          .toDate()
          .getTime() -
        new Timestamp(a.createdDate.seconds, a.createdDate.nanoseconds)
          .toDate()
          .getTime()
      );
    } else if (a.createdDate) {
      return -1;
    } else {
      return 1;
    }
  };

  const sortedTaskList = isCompletedFilter
    ? taskList.filter(task => task.isArchived).sort(sortFn)
    : taskList.filter(task => !task.isArchived).sort(sortFn);

  return (
    <div className="task-wrapper">
      <WidgetHeaderContent
        headerElements={
          <Fragment>
            <div className="task-header">
              <H
                size={headerSizes.EXTRA_SMALL}
                className="header-layout-font"
                usePermanentAccent={false}
              >
                Tasks
              </H>
              <ClickableText
                onClick={onCompletionFilterClick}
                className="body-layout-font"
              >
                {isCompletedFilter ? "Active Tasks" : "Archived Tasks"}
              </ClickableText>
            </div>
            {getHeader(isCompletedFilter)}
          </Fragment>
        }
        contentElements={getPage(sortedTaskList, isCompletedFilter)}
      />
    </div>
  );
}
